<template>
  <div>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-card class="mx-auto">
      <v-data-table
        :headers="headers"
        :items="incidents"
        :page="page"
        :pageCount="numberOfPages"
        :items-per-page="itemsPerPage"
        class="elevation-2 striped"
        v-model="selectedRows"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-minus',
          nextIcon: 'mdi-plus',
          'items-per-page-text': `${perPageText}`,
        }"
        :loading="loadTable"
        :loading-text="$t('common.loadingText')"
      >
        <template v-slot:body.prepend>
          <tr>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="incidentName"
                type="text"
                :label="$t('_incident.incidentType')"
              ></v-text-field>
            </td>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="description"
                type="text"
                :label="$t('_incident.description')"
              ></v-text-field>
            </td>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="countyName"
                type="text"
                :label="$t('_incident.county')"
              ></v-text-field>
            </td>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="neighborhoodName"
                type="text"
                :label="$t('_incident.neighborhood')"
              ></v-text-field>
            </td>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="lat"
                type="number"
                :label="$t('_incident.lat')"
              ></v-text-field>
            </td>
            <td class="d-inline-block d-sm-table-cell">
              <v-text-field
                v-model="lon"
                type="number"
                :label="$t('_incident.lon')"
              ></v-text-field>
            </td>
          </tr>
        </template>
        <template v-slot:item="{ item }">
          <tr
            :style="
              selectedRows.indexOf(item.id) > -1
                ? 'background-color:#82DAC0'
                : ''
            "
            @click="rowClick(item)"
          >
            <td>{{ item.incidentType.name }}</td>
            <td>{{ item.description }}</td>
            <td>{{ item.countyName }}</td>
            <td>{{ item.neighborhoodName }}</td>
            <td>{{ item.lat }}</td>
            <td>{{ item.lon }}</td>
            <td>{{ item.startTimeTable }}</td>
            <td>{{ item.endTimeTable }}</td>
            <td align="right">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon small @click="editItem(item)" color="green">
                      mdi-pencil
                    </v-icon></v-btn
                  >
                </template>
                <span>{{ $t("actions.edit") }}</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon small @click="deleteItem(item)" color="error">
                      mdi-delete
                    </v-icon></v-btn
                  >
                </template>
                <span>{{ $t("actions.delete") }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <v-card-text>{{ $t("common.dataNotFound") }}</v-card-text>
        </template>
      </v-data-table>

      <new-incident-button
        :dialog.sync="dialog"
        :editedItem="editedItem"
        :editedIndex="editedIndex"
        :counties="counties"
        :neighborhoods="neighborhoods"
        :incidentTypes="incidentTypes"
        @confirmed="onConfirmed"
        @cancelled="onCancelled"
      />
    </v-card>
  </div>
</template>

<script>
import NewIncidentButton from "./NewIncidentButton";
import { mapActions, mapGetters } from "vuex";

export default {
  components: { NewIncidentButton },
  props: {
    groupId: String,
    filter: Object,
    filterEndDate: Date,
    counties: Array,
    neighborhoods: Array,
    incidentItems: Array,
  },
  data() {
    return {
      editedIndex: -1,
      editedItem: {
        incidentType: {
          id: "",
          name: "",
        },
        description: "",
        countyCode: null,
        neighborhoodCode: "",
        lat: "",
        lon: "",
        startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        startTime: "",
        endTime: "",
      },
      incidentName: "",
      description: "",
      countyName: "",
      neighborhoodName: "",
      lat: "",
      lon: "",
      startDate: "",
      endDate: "",
      categories: [],
      totalCount: 0,
      page: 0,
      numberOfPages: 5,
      itemsPerPage: 5,
      totalItemsPerPage: 10000,
      sortField: "endTime",
      sortOrder: -1,
      options: {},
      selectedRows: [],
      loadTable: true,
      overlay: true,
    };
  },
  async created() {
    console.log("incidents are refreshing due to creating");
    await this.refreshTable();
  },
  computed: {
    ...mapGetters({ items: "incidents/items" }),
    ...mapGetters({ incidentTypes: "incidents/incidentTypes" }),
    ...mapGetters({ language: "app/language" }),
    lang() {
      return this.language.replaceAll("_", "-");
    },
    headers() {
      return [
        {
          text: this.$t("_incident.incidentType"),
          sortable: true,
          value: "incidentType.name",
        },
        {
          text: this.$t("_incident.description"),
          sortable: false,
          value: "description",
        },
        {
          text: this.$t("_incident.county"),
          sortable: true,
          value: "countyName",
        },
        {
          text: this.$t("_incident.neighborhood"),
          sortable: true,
          value: "neighborhoodName",
        },
        {
          text: this.$t("_incident.lat"),
          sortable: false,
          value: "lat",
        },
        {
          text: this.$t("_incident.lon"),
          sortable: false,
          value: "lon",
        },
        {
          text: this.$t("_incident.startTime"),
          sortable: true,
          value: "startTime",
        },
        {
          text: this.$t("_incident.endTime"),
          sortable: true,
          value: "endTime",
          divider: true,
        },
        {
          text: this.$t("_incident.actions"),
          value: "actions",
          sortable: false,
          align: "right",
          divider: false,
          width: 130,
        },
      ];
    },
    perPageText() {
      if (this.language == "en_US") {
        return "Rows per page:";
      }
      return "Sayfa başına satır:";
    },
    incidents() {
      let conditions = [];

      if (this.incidentName) {
        conditions.push(this.filterIncidentName);
      }
      if (this.description) {
        conditions.push(this.filterDescription);
      }
      if (this.countyName) {
        conditions.push(this.filterCountyName);
      }
      if (this.neighborhoodName) {
        conditions.push(this.filterNeighborhoodName);
      }
      if (this.lat) {
        conditions.push(this.filterLat);
      }
      if (this.lon) {
        conditions.push(this.filterLon);
      }

      if (conditions.length > 0) {
        return this.incidentItems.filter((item) => {
          return conditions.every((condition) => {
            return condition(item);
          });
        });
      }
      return this.incidentItems;
    },
  },
  methods: {
    ...mapActions(["incidents/getItems"]),
    ...mapActions({
      removePlace: "incidents/remove",
      uploadAsset: "places/uploadPlaceAsset",
    }),
    filterIncidentName(item) {
      return item.incidentType.name
        .toLowerCase()
        .includes(this.incidentName.toLowerCase());
    },
    filterDescription(item) {
      return item.description
        .toLowerCase()
        .includes(this.description.toLowerCase());
    },
    filterCountyName(item) {
      return item.countyName
        .toLowerCase()
        .includes(this.countyName.toLowerCase());
    },
    filterNeighborhoodName(item) {
      return item.neighborhoodName
        .toLowerCase()
        .includes(this.neighborhoodName.toLowerCase());
    },
    filterLat(item) {
      return item.lat.toString().toLowerCase().includes(this.lat.toLowerCase());
    },
    filterLon(item) {
      return item.lon.toString().toLowerCase().includes(this.lon.toLowerCase());
    },
    async refreshTable() {
      let filterr = JSON.parse(JSON.stringify(this.filter));
      filterr.fetchDate = this.filterEndDate;
      await this.$store.dispatch("incidents/getItems", {
        page: this.page,
        itemsPerPage: this.totalItemsPerPage,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        filter: filterr,
      });
      this.loadTable = false;
      this.overlay = false;
    },
    fileUploaded() {
      // hide a dialog
      this.refreshTable();
      // save a user and refresh a table
    },
    async deleteItem(item) {
      var self = this;
      self.loading = true;
      self
        .$swal({
          title: "Olayı silmek istediğinizden emin misiniz ?",
          text: "Tekrar geri alamayacaksınız.",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Sil",
          cancelButtonText: "Vazgeç",
          confirmButtonColor: "#ff7674",
          cancelButtonColor: "#41b882",
          showCloseButton: true,
          showLoaderOnConfirm: true,
        })
        .then(async function (isConfirm) {
          if (isConfirm.value) {
            self.loading = true;
            try {
              await self.removePlace({
                item: item,
              });
              self.snackbar = true;
              self.snackColor = "success";
              self.snackText = "Olay silindi";
              self.x = "center";
              self.y = "bottom";
              self.timeout = 3000;
            } catch (e) {
              self.snackbar = true;
              self.snackColor = "error";
              self.snackText = "Olay silinemedi";
              self.x = "center";
              self.y = "bottom";
              self.timeout = 3000;

              console.error(e);
            }
          } else {
            self.loading = false;
          }
        });
    },
    async editItem(item) {
      console.log(item);
      this.editedIndex = this.incidents.indexOf(item);
      this.editedItem = Object.assign({}, item);

      this.editedItem.startDate = new Date(
        item.startTime.getTime() - item.startTime.getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(0, 10);

      this.editedItem.endDate = new Date(
        item.endTime.getTime() - item.endTime.getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(0, 10);

      this.editedItem.startTime = new Date(
        item.startTime.getTime() - item.startTime.getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(11, 16);

      this.editedItem.endTime = new Date(
        item.endTime.getTime() - item.endTime.getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(11, 16);

      console.log(item.incidentType.name);
      this.editedItem.name = item.incidentType.name;
      this.dialog = true;
    },
    onConfirmed(editedItem) {
      // hide a dialog
      this.dialog = false;
      this.refreshTable();
      this.$emit("fileUploaded");
      // save a user and refresh a table
    },
    onCancelled() {
      // hide a dialog
      this.dialog = false;
    },
    rowClick(item, row) {
      console.log("row clicked");
      console.log(item.id);
      // row.select(true);
      this.selectedId = item.name;
      this.toggleSelection(item);
      console.log("rowclickkkk");
    },
    toggleSelection(item) {
      if (this.selectedRows.includes(item.id)) {
        console.log(this.selectedRows);
        this.selectedRows = this.selectedRows.filter(
          (selectedKeyID) => selectedKeyID !== item.id
        );
        this.$emit("placeReClick");
      } else if (this.selectedRows.length == 0) {
        this.selectedRows.push(item.id);
        this.$emit("placeClick", item);
      } else if (
        !this.selectedRows.includes(item.id) &&
        this.selectedRows.length == 1
      ) {
        this.selectedRows.pop();
        this.selectedRows.push(item.id);
        this.$emit("placeClick", item);
      }
    },
  },

  watch: {
    // groupId() {
    //   this.$emit("update:groupId", this.groupId);
    //   this.refreshTable();
    // },
    options: {
      handler() {
        this.refreshTable();
      },
      deep: true,
    },
    language: function () {
      console.log("lang changed");
      this.refreshTable();
    },
    filter: {
      handler() {
        this.refreshTable();
      },
      deep: true,
    },
  },
};
</script>